/**
 * Axios-based API client for admin backend.
 * Base URL from VITE_API_BASE_URL (public-only; Security Checklist: no secrets in client bundle).
 * Request interceptor attaches session token (Bearer or X-Session-Token) when available.
 * Global response interceptor shows Arabic toast on failure.
 */

import axios, { type AxiosError, type InternalAxiosRequestConfig, type AxiosResponse } from 'axios';
import { showGlobalToast } from '@contexts/ToastContext';
import { getSessionToken } from '@services/sessionStore';
import { notifyBrokerApiUnauthorized } from '@services/authSessionEvents';
import i18n from '../i18n/config';

const BASE_URL =
  typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL !== undefined
    ? import.meta.env.VITE_API_BASE_URL
    : '';

export const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000,
  /** Required so the browser sends `saee_session` on cross-origin requests (Vite → API). Pair with CORS_ALLOW_CREDENTIALS. */
  withCredentials: true,
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getSessionToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    config.headers['X-Session-Token'] = token;
  }
  return config;
});

/** Connection error message: use actual API host/port from base URL so it's never wrong. */
function getConnectionMessage(): string {
  try {
    const u = new URL(BASE_URL);
    const hostPort = u.port ? `${u.hostname}:${u.port}` : u.hostname;
    return i18n.t('apiErrors.connectionErrorWithHost', {
      defaultValue: `Could not connect to the server. Ensure the server is running on ${hostPort}.`,
      host: hostPort,
    });
  } catch {
    return i18n.t('apiErrors.connectionError', {
      defaultValue: 'Could not connect to the server. Ensure the server is running.',
    });
  }
}

const ERROR_MESSAGES = {
  timeout: () =>
    i18n.t('apiErrors.timeout', { defaultValue: 'Request timed out. Try again.' }),
  network: () =>
    i18n.t('apiErrors.network', { defaultValue: 'Network error. Check your connection.' }),
  default: () =>
    i18n.t('apiErrors.default', {
      defaultValue: 'An error occurred while processing your request. Please try again later.',
    }),
} as const;

/**
 * Map Axios errors to user-facing Arabic messages for the global toast.
 * Prefers server message when present; otherwise uses status code or connection/timeout heuristics.
 */
function getErrorMessage(err: AxiosError): string {
  if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
    return ERROR_MESSAGES.timeout();
  }
  if (err.code === 'ERR_NETWORK' || !err.response) {
    return getConnectionMessage();
  }
  const status = err.response?.status;
  const data = err.response?.data as
    | { message?: string; error?: string | { message?: string; code?: string } }
    | undefined;
  const nestedMsg =
    typeof data?.error === 'object' && data?.error && 'message' in data.error
      ? (data.error as { message?: string }).message
      : undefined;
  const serverMsg = nestedMsg ?? data?.message ?? (typeof data?.error === 'string' ? data.error : undefined);
  if (serverMsg && typeof serverMsg === 'string') {
    return serverMsg;
  }
  if (status === 400) return i18n.t('apiErrors.badRequest', { defaultValue: 'Invalid request. Check the input data.' });
  if (status === 401) return i18n.t('apiErrors.unauthorized', { defaultValue: 'You must be signed in to continue.' });
  if (status === 403) return i18n.t('apiErrors.forbidden', { defaultValue: 'You do not have permission for this action.' });
  if (status === 404) return i18n.t('apiErrors.notFound', { defaultValue: 'The requested resource was not found.' });
  if (status && status >= 500) return i18n.t('apiErrors.serverError', { defaultValue: 'A server error occurred. Please try again later.' });
  return ERROR_MESSAGES.default();
}

let last401ToastAt = 0;
const TOAST_401_THROTTLE_MS = 4000;

apiClient.interceptors.response.use(
  (res: AxiosResponse) => res,
  (err: AxiosError) => {
    if (err.config?.skipGlobalErrorToast) {
      return Promise.reject(err);
    }
    const status = err.response?.status;
    const url = err.config?.url ?? '';
    const isAuthMe = url.includes('/api/auth/me');
    /** Auth wizard steps show inline errors; avoid duplicate English server-message toasts. */
    const isAuthOtpForm =
      url.includes('/api/auth/register') ||
      url.includes('/api/auth/verify-otp') ||
      url.includes('/api/auth/complete') ||
      url.includes('/api/auth/national-address') ||
      url.includes('/api/auth/nafath');
    const isPayerRefund = url.includes('/payer-refund/');
    if ((isAuthOtpForm || isPayerRefund) && (status === 400 || status === 403 || status === 401 || status === 409)) {
      return Promise.reject(err);
    }
    const title = getErrorMessage(err);
    if (isAuthMe && (status === 401 || status === 403)) {
      return Promise.reject(err);
    }
    if (status === 401) {
      const path = url.split('?')[0] ?? url;
      const isBrokerScopedApi =
        path.includes('/api/deals') ||
        path.includes('/api/brokerages') ||
        path.includes('/api/wallet') ||
        path.includes('/api/profile') ||
        path.includes('/api/commission-logs');
      if (isBrokerScopedApi) {
        notifyBrokerApiUnauthorized();
      }
      const now = Date.now();
      if (now - last401ToastAt >= TOAST_401_THROTTLE_MS) {
        last401ToastAt = now;
        showGlobalToast({ title, variant: 'error' });
      }
    } else {
      showGlobalToast({ title, variant: 'error' });
    }
    return Promise.reject(err);
  },
);

export default apiClient;
